import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { tokenTrackingService, TokenSwap, TrackingConfig } from '../services/tokenTrackingService';

const TokenTracking: React.FC = () => {
  const [trackedTokens, setTrackedTokens] = useState<TrackingConfig[]>([]);
  const [swapHistory, setSwapHistory] = useState<TokenSwap[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [isTracking, setIsTracking] = useState(false);

  useEffect(() => {
    loadTrackingData();
    setupEventListeners();
  }, []);

  const loadTrackingData = () => {
    setTrackedTokens(tokenTrackingService.getTrackedTokens());
    setSwapHistory(tokenTrackingService.getSwapHistory());
    setStats(tokenTrackingService.getTrackingStats());
    setIsTracking(tokenTrackingService.isTrackingActive());
  };

  const setupEventListeners = () => {
    if (typeof window !== 'undefined') {
      window.addEventListener('tokenSwap', (event: any) => {
        loadTrackingData();
      });
    }
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatValue = (value: string) => {
    return `$${parseFloat(value).toLocaleString()}`;
  };

  const getSwapTypeColor = (type: 'buy' | 'sell') => {
    return type === 'buy' ? 'text-green-400' : 'text-red-400';
  };

  const getSwapTypeIcon = (type: 'buy' | 'sell') => {
    return type === 'buy' ? '📈' : '📉';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-green-400">🎯 Token Tracking</h2>
          <p className="text-green-400/70 text-sm">Monitor token swaps and wallet activities</p>
        </div>
        <Button 
          onClick={loadTrackingData}
          className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30"
        >
          🔄 Refresh
        </Button>
      </div>

      {/* Tracking Status */}
      <Card className="p-4 bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-green-400 font-bold">Tracking Status</h3>
            <p className="text-green-400/70 text-sm">
              {isTracking ? '🟢 Active' : '🔴 Inactive'} • {trackedTokens.length} tokens tracked
            </p>
          </div>
          <div className="text-right">
            <div className="text-green-400 font-bold">{swapHistory.length}</div>
            <div className="text-green-400/70 text-sm">Total Swaps</div>
          </div>
        </div>
      </Card>

      {/* Statistics */}
      {stats && (
        <div className="grid md:grid-cols-4 gap-4">
          <Card className="p-4 bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/30">
            <div className="text-green-400/70 text-sm mb-1">Total Swaps (24h)</div>
            <div className="text-2xl font-bold text-green-400">{stats.totalSwaps}</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/30">
            <div className="text-blue-400/70 text-sm mb-1">Buy Volume</div>
            <div className="text-2xl font-bold text-blue-400">{formatValue(stats.totalBuyVolume)}</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/30">
            <div className="text-red-400/70 text-sm mb-1">Sell Volume</div>
            <div className="text-2xl font-bold text-red-400">{formatValue(stats.totalSellVolume)}</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/30">
            <div className="text-purple-400/70 text-sm mb-1">Net Flow</div>
            <div className={`text-2xl font-bold ${parseFloat(stats.netFlow) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {parseFloat(stats.netFlow) >= 0 ? '+' : ''}{formatValue(stats.netFlow)}
            </div>
          </Card>
        </div>
      )}

      {/* Tracked Tokens */}
      <div>
        <h3 className="text-lg font-bold text-green-400 mb-4">📊 Tracked Tokens</h3>
        <div className="space-y-3">
          {trackedTokens.map((config, index) => (
            <Card key={index} className="p-4 bg-gradient-to-br from-[#0d0d2a] to-[#0a0a1a] border-green-500/30">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="text-green-300 font-medium">Token Contract</div>
                  <div className="text-green-400/70 text-sm font-mono">{config.tokenAddress}</div>
                  <div className="text-green-300 font-medium mt-2">Wallet Tracker</div>
                  <div className="text-green-400/70 text-sm font-mono">{config.walletAddress}</div>
                </div>
                <div className="text-right">
                  <div className="text-green-400 font-bold">${config.minAmount}+</div>
                  <div className="text-green-400/70 text-sm">Min Amount</div>
                  <div className={`text-xs px-2 py-1 rounded-full font-medium mt-2 ${
                    config.enabled ? 'text-green-400 bg-green-400/20' : 'text-red-400 bg-red-400/20'
                  }`}>
                    {config.enabled ? 'ACTIVE' : 'INACTIVE'}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Recent Swaps */}
      <div>
        <h3 className="text-lg font-bold text-green-400 mb-4">🔄 Recent Swaps</h3>
        {swapHistory.length === 0 ? (
          <Card className="p-8 text-center">
            <div className="text-green-400/50 text-lg mb-2">No swaps detected yet</div>
            <div className="text-green-400/30 text-sm">
              Swaps will appear here when detected
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {swapHistory.slice(0, 10).map((swap, index) => (
              <Card key={index} className="p-4 bg-gradient-to-br from-[#0d0d2a] to-[#0a0a1a] border-green-500/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl">{getSwapTypeIcon(swap.type)}</div>
                    <div>
                      <div className={`font-medium ${getSwapTypeColor(swap.type)}`}>
                        {swap.type.toUpperCase()} • {swap.amount} tokens
                      </div>
                      <div className="text-green-400/70 text-sm">
                        {swap.dex} • {formatTime(swap.timestamp)}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-green-400 font-bold">{formatValue(swap.value)}</div>
                    <div className="text-green-400/70 text-sm">${swap.price} per token</div>
                  </div>
                </div>
                <div className="mt-2 text-xs text-green-400/50 font-mono">
                  {swap.hash}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Instructions */}
      <Card className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30">
        <h3 className="text-blue-400 font-bold mb-2">📋 How to Use Token Tracking</h3>
        <div className="text-blue-400/70 text-sm space-y-1">
          <div>1. Ask the AI: "Track this token 0x4675c7e5baafbffbca748158becba61ef3b0a263"</div>
          <div>2. The system will monitor all swaps for the specified wallet</div>
          <div>3. Get real-time alerts when large transactions occur</div>
          <div>4. Use the data to make informed trading decisions</div>
        </div>
      </Card>
    </div>
  );
};

export default TokenTracking;
